import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Table, Tooltip, theme } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { toLower, toUpper } from 'lodash';
import moment from 'moment';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import IconEdit from '@/assets/icon-edit.svg?react';
import IconVisibility from '@/assets/visibility.svg?react';
import { EPButton } from '@/components';

import TitleColumn from './TitleColumn';
import Severity from './Severity';
import DeleteRuleColumn from './DeleteRuleColumn';
import ActivateAlertRuleColumn from './ActivateAlertRuleColumn';

const CUSTOM_ALERT_TYPE = 'CUSTOM';
const MANUFACTURER_ALERT_TYPE = 'MANUFACTURER';

const { useToken } = theme;

const RulesTable = observer(({ rules, isSingleMode }) => {
  const location = useLocation();
  const params = useParams();
  const { rulesStore, permissionStore } = useStores();
  const { t } = useTranslation();
  const { token } = useToken();

  // change default placement of tooltips to entire table
  Tooltip.defaultProps = {};
  Tooltip.defaultProps.placement = 'topLeft';

  const columns = [
    {
      dataIndex: 'state',
      key: 'state',
      title: t('Rule activation status'),
      width: '5%',
      className: 'column-state',
      ellipsis: false,
      colSpan: 2,
      render: (state, alertRule) => <ActivateAlertRuleColumn alertRule={alertRule} />,
      sorter: (a, b) => {
        const titleA = toUpper(a.title);
        const titleB = toUpper(b.title);
        if (titleA < titleB) return -1;
        if (titleA > titleB) return 1;
        return 0;
      },
    },
    {
      dataIndex: 'title',
      key: 'title',
      width: '65%',
      title: null,
      className: 'column-title',
      ellipsis: true,
      colSpan: 0,
      render: (title, alertRule) => <TitleColumn title={title} alertRule={alertRule} />,
    },
    {
      dataIndex: 'severity',
      key: 'severity',
      title: t('Severity'),
      width: '10%',
      className: 'column-severity',
      ellipsis: true,
      render: (severity, alertRule) => <Severity alertRule={alertRule} />,
      sorter: (a, b) => {
        if (toUpper(a.severity) === 'SHUTDOWN') return 1;
        if (toUpper(b.severity) === 'SHUTDOWN') return -1;
        if (toUpper(a.severity) === 'SERVICE' && toUpper(b.severity) === 'WARNING') return -1;
        if (toUpper(a.severity) === 'WARNING' && toUpper(b.severity) === 'SERVICE') return 1;
        return 0;
      },
    },
    {
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      title: t('Last updated'),
      width: '12%',
      className: 'column-date',
      ellipsis: true,
      render: lastUpdated => (
        <div className="rule-item-date-created">{lastUpdated && moment(lastUpdated).format('MMM D, YYYY')}</div>
      ),
      defaultSortOrder: 'descend',
      sorter: (a, b) => moment(a.lastUpdated).valueOf() - moment(b.lastUpdated).valueOf(),
    },
    {
      dataIndex: 'alertType',
      key: 'edit',
      title: null,
      width: '4%',
      className: 'column-edit',
      ellipsis: true,
      render: (alertType, rule) => {
        const disabled = (() => {
          switch (toUpper(alertType)) {
            case CUSTOM_ALERT_TYPE:
              return !permissionStore.isAuth(permissions.UPDATE_CUSTOM_ALERT);

            case MANUFACTURER_ALERT_TYPE:
              return !permissionStore.isAuth(permissions.UPDATE_MANUFACTURER_ALERT);

            default:
              return true;
          }
        })();

        const getIcon = () => {
          switch (toUpper(alertType)) {
            case CUSTOM_ALERT_TYPE:
              return <IconEdit />;

            case MANUFACTURER_ALERT_TYPE:
              return permissionStore.isAuth(permissions.UPDATE_MANUFACTURER_ALERT) ? <IconEdit /> : <IconVisibility />;

            default:
              return true;
          }
        };

        return (
          <div className="rule-item-edit">
            {!rule.complexFormula && (
              <Link
                to={`/home/station/${params.stationId}/rules/${toLower(alertType)}/${rule.id}/edit`}
                state={{ from: location.pathname }}
              >
                <EPButton className="action-button" type="link" icon={getIcon()} disabled={disabled} />
              </Link>
            )}
          </div>
        );
      },
    },
    {
      dataIndex: 'alertType',
      key: 'delete',
      title: null,
      width: '4%',
      className: 'column-delete',
      ellipsis: true,
      render: (alertType, alertRule) => <DeleteRuleColumn alertRule={alertRule} isSingleMode={isSingleMode} />,
    },
  ];

  return (
    <StyledTable
      columns={columns}
      dataSource={rules}
      bordered={false}
      showHeader={true}
      pagination={{
        position: ['bottomCenter'],
        defaultPageSize: 50,
        hideOnSinglePage: true,
        showSizeChanger: false,
        showQuickJumper: false,
        itemRender: (page, type) => {
          if (type === 'prev') {
            return <IconChevronDown style={{ transform: 'rotate(90deg) translateX(20%)' }} />;
          } else if (type === 'next') {
            return <IconChevronDown style={{ transform: 'rotate(-90deg) translateX(-20%)' }} />;
          } else if (type === 'page') {
            return page;
          }
        },
      }}
      rowKey={({ id }) => id}
      locale={{
        emptyText: <span>{t('No rules to display')}</span>,
      }}
      scroll={{ y: 'max-content' }}
      loading={{
        spinning: rulesStore.isLoading,
        indicator: <LoadingOutlined spin />,
        size: 'large',
        style: {
          backgroundColor: token.colorBgBase,
        },
      }}
    />
  );
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledTable: React.FC<any> = styled(Table)`
  &.ant-table-wrapper {
    height: 100%;

    .ant-spin-nested-loading {
      height: 100%;

      > div > .ant-spin {
        max-height: initial;
      }

      > .ant-spin-container {
        height: 100%;
        display: flex;
        flex-direction: column;
        transition: initial;

        &::after {
          transition: initial;
        }
      }
    }

    .ant-table {
      height: 100%;
      overflow: auto;

      background-color: ${({ theme: { token } }) => token.colorBgLayout};
      color: ${({ theme: { token } }) => token.colorText};

      .ant-table-thead > tr > th[colspan]:not([colspan='1']) {
        text-align: left;
      }

      .ant-table-thead > tr > th:hover {
        background: ${({ theme: { token } }) => token.colorBgLayout};

        .ant-table-column-sorter {
          color: ${({ theme: { token } }) => token.colorBorder};
        }
      }

      .ant-table-column-sorter {
        color: ${({ theme: { token } }) => token.colorBorder};
      }

      .ant-table-cell::before {
        display: none;
      }

      .ant-table-container {
        height: 100%;
        display: flex;
        flex-direction: column;

        .ant-table-header {
          flex-shrink: 0;

          .ant-table-cell-scrollbar {
            visibility: collapse;
          }
        }

        .ant-table-body {
          margin-top: ${({ theme: { token } }) => token.marginLG}px;
          flex-shrink: 1;
        }
      }

      .ant-table-cell-scrollbar {
        background-color: unset;
      }

      .column-state,
      .column-delete,
      .column-edit,
      .column-date,
      .column-severity,
      .column-title {
        background-color: unset;
        color: ${({ theme: { token } }) => token.colorText};
        font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
        text-align: left;
      }

      .ant-table-row {
        .ant-table-cell {
          border-bottom: none;
          vertical-align: top;
          padding: 0px ${({ theme: { token } }) => `${token.paddingXL}px ${token.paddingMD}px`} 0px;

          &.column-title {
            min-width: 300px;
          }

          .rule-description {
            display: flex;
            flex-direction: column;

            .rule-item-title {
              color: ${({ theme: { token } }) => token.colorText};
              font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
            }

            .rule-item-detail {
              color: ${({ theme: { token } }) => token.colorTextSecondary};
              font-size: ${({ theme: { token } }) => token.fontSize}px;
              white-space: normal;
            }
          }

          .rule-item-severity {
            display: flex;
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            justify-items: center;
            border-radius: 1.3px;
            height: 20px;

            .severity-icon {
              svg {
                height: 20px;
                width: 20px;
              }
            }

            .severity-label {
              color: ${({ theme: { token } }) => token.colorText};
              background-color: ${({ theme: { token } }) => token.colorBgElevated};
              padding: 0 ${({ theme: { token } }) => token.paddingXS}px;
            }
          }

          .rule-item-alert-type {
            display: inline-block;
            color: ${({ theme: { token } }) => token.colorText};
            background-color: ${({ theme: { token } }) => token.colorBgElevated};
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            padding: 0 ${({ theme: { token } }) => token.paddingXS}px;
            margin-top: ${({ theme: { token } }) => token.marginXXS}px;
          }

          .rule-item-date-created {
            color: ${({ theme: { token } }) => token.colorText};
            font-size: ${({ theme: { token } }) => token.fontSize}px;
          }

          .rule-item-edit {
            color: ${({ theme: { token } }) => token.colorText};
          }
        }
      }
    }

    .ant-table-tbody > tr.ant-table-row:hover > td {
      background: unset;
    }

    .ant-table-tbody > tr.ant-table-placeholder:hover > td {
      background: unset;
    }

    .ant-table-tbody > tr.ant-table-placeholder > td {
      color: ${({ theme: { token } }) => token.colorBorder};
      text-align: center;
    }

    .ant-spin-blur {
      opacity: 0;
    }

    .ant-switch-small {
      background-color: ${({ theme: { token } }) => token.colorBgContainer};
      height: 10px;

      .ant-switch-loading-icon {
        top: 30%;
      }

      .ant-click-animating-node {
        animation: unset;
      }

      .ant-switch-handle::before {
        background-color: ${({ theme: { token } }) => token.colorBorder};
        top: -5px;
        left: -2px;
        height: 16px;
        width: 16px;
      }
    }

    .ant-switch-checked {
      .ant-switch-handle::before {
        background-color: ${({ theme: { token } }) => token.colorPrimary};
      }

      &:focus {
        box-shadow: unset;
      }
    }

    .ant-pagination {
      .ant-pagination-prev,
      .ant-pagination-next,
      .ant-pagination-item {
        background-color: ${({ theme: { token } }) => token.colorBgContainer};
        border-color: ${({ theme: { token } }) => token.colorBgContainer};
        color: ${({ theme: { token } }) => token.colorBorder};
      }

      .ant-pagination-item-active {
        background-color: ${({ theme: { token } }) => token.colorPrimary};
        border-color: ${({ theme: { token } }) => token.colorPrimary};
        color: ${({ theme: { token } }) => token.colorText};
      }

      .ant-pagination-jump-next,
      .ant-pagination-jump-prev {
        display: none;
      }
    }
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover + tr.ant-table-row > td {
    border-top-color: transparent;
    border-bottom-color: transparent;
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr:last-child > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:last-child:hover > td {
    border-top-color: transparent;
    border-bottom-color: transparent;
  }
`;

export default RulesTable;
